import { countryName } from '@trips/refdata';
import {
  type Alliance,
  CABIN_LABELS,
  type CabinClass,
  COUNTRIES_TOTAL,
  type FlightRecord,
  type FlightRef,
  type FlightStats,
  flagEmoji,
  flightRecords,
  flightStats,
  flightYears,
  type SeatPosition,
} from '@trips/shared/flight-stats';
import {
  formatClock,
  formatDistance,
  formatDuration,
  formatFullDate,
  formatNumber,
  monthName,
  plural,
  weekdayName,
} from '@trips/shared/format';
import type { TripLeg } from '@trips/shared/trip-model';
import { type ReactNode, useMemo, useState } from 'react';
import {
  selectLiveItems,
  selectPlaceLookup,
  selectPrefs,
  selectTripSummaries,
} from '../../data/selectors';
import { useStore } from '../../data/store';
import { now } from '../../lib/clock';
import { navigate } from '../../lib/router';
import { EmptyState } from '../../ui/EmptyState';
import { Icon, type IconName } from '../../ui/Icon';
import { MiniMap } from '../../ui/MiniMap';
import { Page } from '../../ui/Page';
import { Segmented } from '../../ui/Segmented';
import { useFriendFlights, useFriends } from '../friends/friends';
import { AreaChart, Donut, Legend, Meter, type Slice, SplitBar } from './Charts';
import s from './StatsScreen.module.css';
import { TravelStats } from './TravelStats';

type Prefs = ReturnType<typeof selectPrefs>;
type ChartBy = 'year' | 'month' | 'weekday';

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumSignificantDigits: 3,
});
const pct = (share: number) => `${Math.round(share * 100)}%`;
const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

/** Two decimals under one, one under ten, whole numbers after that. */
function short(n: number): string {
  if (n < 1) return n.toFixed(2);
  return n < 10 ? n.toFixed(1) : formatNumber(Math.round(n));
}

function Card({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={s.card}>
      <header className={s.cardHead}>
        <h2 className="t-headline">{title}</h2>
        {aside != null && <span className={`${s.aside} num`}>{aside}</span>}
      </header>
      {children}
    </section>
  );
}

function Metric({
  value,
  label,
  note,
  big,
}: {
  value: ReactNode;
  label: ReactNode;
  note?: ReactNode;
  big?: boolean;
}) {
  return (
    <div className={s.metric}>
      <span className={`${big ? s.big : s.value} num`}>{value}</span>
      <span className={s.label}>{label}</span>
      {note && <span className={s.note}>{note}</span>}
    </div>
  );
}

function Route({ from, to }: { from: string; to: string }) {
  return (
    <span className={`${s.route} wide`}>
      {from}
      <Icon name="airplane-tilt" size={13} className={s.routePlane} />
      {to}
    </span>
  );
}

interface RankRow {
  key: string;
  title: ReactNode;
  detail?: ReactNode;
  lead?: ReactNode;
  count: number;
}

const SHOWN = 5;

/** A ranked list with bars, the top five first and the rest behind Show more. */
function Ranked({ rows }: { rows: RankRow[] }) {
  const [open, setOpen] = useState(false);
  const max = rows[0]?.count || 1;
  const shown = open ? rows : rows.slice(0, SHOWN);
  return (
    <>
      <ol className={s.rank}>
        {shown.map((r) => (
          <li key={r.key} className={s.rankRow}>
            {r.lead != null && <span className={s.lead}>{r.lead}</span>}
            <span className={s.rankText}>
              <span className={s.rankTitle}>{r.title}</span>
              {r.detail != null && <span className={s.rankDetail}>{r.detail}</span>}
            </span>
            <span className={`${s.rankCount} num`}>{formatNumber(r.count)}</span>
            <span className={s.rankMeter}>
              <Meter share={r.count / max} />
            </span>
          </li>
        ))}
      </ol>
      {rows.length > SHOWN && (
        <button type="button" className={s.more} onClick={() => setOpen(!open)}>
          {open ? 'Show fewer' : `Show ${formatNumber(rows.length - SHOWN)} more`}
          <Icon name={open ? 'caret-up' : 'caret-down'} size={14} />
        </button>
      )}
    </>
  );
}

/** A flight called out by name: longest, shortest, worst delay. */
function FlightLine({
  label,
  flight,
  value,
  prefs,
}: {
  label: string;
  flight: FlightRef;
  value: ReactNode;
  prefs: Prefs;
}) {
  return (
    <div className={s.flight}>
      <span className={s.label}>{label}</span>
      <div className={s.flightRow}>
        <Route from={flight.fromCode} to={flight.toCode} />
        <span className={`${s.flightValue} num`}>{value}</span>
      </div>
      <span className={s.note}>
        {flight.airlineName} {flight.flightNumber}, {formatFullDate(flight.date, prefs)}
      </span>
    </div>
  );
}

function TimeCard({ st, prefs }: { st: FlightStats; prefs: Prefs }) {
  const days = st.minutes / 1440;
  return (
    <Card title="Time and distance">
      <div className={s.pair}>
        <Metric
          big
          value={formatNumber(Math.round(st.minutes / 60))}
          label="Hours in the air"
          note={`${short(days)} days, or ${short(days / 30.44)} months`}
        />
        <Metric
          big
          value={formatDistance(st.miles, prefs)}
          label="Flown"
          note={
            <>
              {short(st.laps)} times around the Earth
              <br />
              {short(st.moons)} times to the Moon
            </>
          }
        />
      </div>
    </Card>
  );
}

function FlightsCard({ st, prefs }: { st: FlightStats; prefs: Prefs }) {
  const [by, setBy] = useState<ChartBy>('year');
  const mode: ChartBy = st.year != null && by === 'year' ? 'month' : by;
  const chart =
    mode === 'year'
      ? {
          values: st.perYear.map((y) => y.count),
          labels: st.perYear.map((y) => `’${String(y.key).slice(2)}`),
        }
      : mode === 'month'
        ? { values: st.perMonth, labels: st.perMonth.map((_, i) => monthName(i + 1).charAt(0)) }
        : {
            values: st.perWeekday,
            labels: st.perWeekday.map((_, i) => weekdayName(i).slice(0, 2)),
          };

  const badges: { icon: IconName; title: string; text: string }[] = [];
  if (st.daytimeShare != null) {
    badges.push(
      st.daytimeShare >= 0.5
        ? {
            icon: 'sun',
            title: 'Daytime flyer',
            text: `${pct(st.daytimeShare)} of flights leave between ${formatClock(6, 0, prefs)} and ${formatClock(19, 0, prefs)}`,
          }
        : {
            icon: 'moon-stars',
            title: 'Night flyer',
            text: `${pct(1 - st.daytimeShare)} of flights leave in the evening or at night`,
          },
    );
  }
  if (st.topSeason)
    badges.push({
      icon: 'sun-horizon',
      title: `${cap(st.topSeason.season)} is your season`,
      text: `${pct(st.topSeason.share)} of flights are in ${st.topSeason.season}`,
    });
  if (st.topWeekday)
    badges.push({
      icon: 'calendar-blank',
      title: `${weekdayName(st.topWeekday.weekday, true)}s are busiest`,
      text: `${pct(st.topWeekday.share)} of flights leave on a ${weekdayName(st.topWeekday.weekday, true)}`,
    });
  if (st.redEyes)
    badges.push({
      icon: 'moon',
      title: plural(st.redEyes, 'red-eye'),
      text: 'Overnight flights leaving after 9 PM',
    });

  return (
    <Card title="Flights">
      <div className={s.trio}>
        <Metric big value={formatNumber(st.flights)} label="Flights" />
        <Metric value={formatNumber(st.international)} label="International" />
        <Metric value={formatNumber(st.domestic)} label="Domestic" />
      </div>
      {st.cancelled > 0 && (
        <p className={s.note}>Plus {plural(st.cancelled, 'cancelled flight')}, not counted here.</p>
      )}
      <Segmented
        label="Flights by"
        value={mode}
        onChange={setBy}
        options={[
          ...(st.year == null ? [{ value: 'year' as const, label: 'Year' }] : []),
          { value: 'month', label: 'Month' },
          { value: 'weekday', label: 'Weekday' },
        ]}
      />
      <AreaChart values={chart.values} labels={chart.labels} label={`Flights by ${mode}`} />
      {st.busiest && (
        <div className={s.pair}>
          <Metric
            value={st.busiest.label}
            label="Busiest"
            note={plural(st.busiest.count, 'flight')}
          />
          {st.slowest && (
            <Metric
              value={st.slowest.label}
              label="Quietest"
              note={plural(st.slowest.count, 'flight')}
            />
          )}
        </div>
      )}
      {badges.length > 0 && (
        <ul className={s.badges}>
          {badges.map((b) => (
            <li key={b.title} className={s.badge}>
              <span className={s.badgeIcon}>
                <Icon name={b.icon} size={18} />
              </span>
              <span className={s.rankText}>
                <span className={s.rankTitle}>{b.title}</span>
                <span className={s.rankDetail}>{b.text}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function FlightTimeCard({ st, prefs }: { st: FlightStats; prefs: Prefs }) {
  const sizeOf = (f: FlightRef) =>
    [
      f.miles != null && formatDistance(f.miles, prefs),
      f.minutes != null && formatDuration(f.minutes),
    ]
      .filter(Boolean)
      .join(', ');
  return (
    <Card title="Flight time">
      <div className={s.pair}>
        <Metric
          value={st.avgMinutes != null ? formatDuration(st.avgMinutes) : 'None'}
          label="Average flight"
        />
        <Metric
          value={st.avgMiles != null ? formatDistance(st.avgMiles, prefs) : 'None'}
          label="Average distance"
        />
      </div>
      {st.longest && (
        <FlightLine label="Longest" flight={st.longest} value={sizeOf(st.longest)} prefs={prefs} />
      )}
      {st.shortest && (
        <FlightLine
          label="Shortest"
          flight={st.shortest}
          value={sizeOf(st.shortest)}
          prefs={prefs}
        />
      )}
    </Card>
  );
}

function DelaysCard({ st, prefs }: { st: FlightStats; prefs: Prefs }) {
  const d = st.delays;
  if (d.timed === 0 && d.cancelled === 0)
    return (
      <Card title="Delays">
        <p className={s.note}>
          Delays come from live flight tracking, which only covers recent flights. None of these
          have it yet.
        </p>
      </Card>
    );
  const rows: { key: string; label: string; count: number; tone: string }[] = [
    { key: 'early', label: 'Early', count: d.early, tone: 'var(--act)' },
    { key: 'on', label: 'On time', count: d.onTime, tone: 'var(--ok)' },
    { key: 'late', label: 'Delayed', count: d.delayed, tone: 'var(--warn)' },
    { key: 'cancelled', label: 'Cancelled', count: d.cancelled, tone: 'var(--bad)' },
  ];
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card title="Delays">
      <div className={s.pair}>
        <Metric big value={formatDuration(d.totalMinutes)} label="Lost to delays" />
        <Metric
          big
          value={d.timed ? pct(d.delayed / d.timed) : 'None'}
          label="Arrived 15+ min late"
        />
      </div>
      <ul className={s.bars}>
        {rows.map((r) => (
          <li key={r.key} className={s.barRow}>
            <span className={s.barLabel}>{r.label}</span>
            <Meter share={r.count / max} tone={r.tone} />
            <span className={`${s.rankCount} num`}>{formatNumber(r.count)}</span>
          </li>
        ))}
      </ul>
      {d.worst && (
        <FlightLine
          label="Worst delay"
          flight={d.worst}
          value={formatDuration(d.worst.delay)}
          prefs={prefs}
        />
      )}
      <p className={s.note}>
        From {plural(d.timed, 'flight')} with timing data. Early is 5 or more minutes ahead.
      </p>
    </Card>
  );
}

const SEAT_SLICES: { key: SeatPosition; label: string; color: string }[] = [
  { key: 'window', label: 'Window', color: 'var(--shade-1)' },
  { key: 'middle', label: 'Middle', color: 'var(--shade-3)' },
  { key: 'aisle', label: 'Aisle', color: 'var(--shade-2)' },
];
const CABIN_ORDER: CabinClass[] = ['economy', 'premium', 'business', 'first'];
const CABIN_COLORS: Record<CabinClass, string> = {
  economy: 'var(--shade-1)',
  premium: 'var(--shade-2)',
  business: 'var(--shade-3)',
  first: 'var(--shade-4)',
};

function SeatCard({ st }: { st: FlightStats }) {
  const seats: Slice[] = SEAT_SLICES.map((x) => ({ ...x, value: st.seats[x.key] }));
  const cabins: Slice[] = CABIN_ORDER.map((c) => ({
    key: c,
    label: CABIN_LABELS[c],
    value: st.cabins[c],
    color: CABIN_COLORS[c],
  }));
  const top = [...cabins].sort((a, b) => b.value - a.value)[0]!;
  return (
    <Card title="Seat and class">
      {st.seatKnown > 0 ? (
        <div className={s.stack}>
          <h3 className={s.label}>Seat</h3>
          <SplitBar slices={seats} />
          <Legend slices={seats} />
          <span className={s.note}>From {plural(st.seatKnown, 'flight')} with a seat number.</span>
        </div>
      ) : (
        <p className={s.note}>No seat numbers on these flights.</p>
      )}
      {st.cabinKnown > 0 ? (
        <div className={s.stack}>
          <h3 className={s.label}>Class</h3>
          <div className={s.donutRow}>
            <Donut
              slices={cabins}
              center={pct(top.value / st.cabinKnown)}
              caption={top.key === 'premium' ? 'Premium' : top.label}
            />
            <Legend slices={cabins} />
          </div>
          <span className={s.note}>From {plural(st.cabinKnown, 'flight')} with a cabin.</span>
        </div>
      ) : (
        <p className={s.note}>No cabins on these flights.</p>
      )}
    </Card>
  );
}

function TripsCard({ year }: { year: number | null }) {
  const trips = useStore(selectTripSummaries);
  const stats = useMemo(() => {
    const scoped = trips.filter(
      (t) => t.phase !== 'upcoming' && (year == null || t.startDate?.startsWith(String(year))),
    );
    let days = 0;
    let nights = 0;
    const cities = new Set<string>();
    for (const t of scoped) {
      days += t.days;
      nights += t.nights;
      for (const c of t.places) cities.add(c);
    }
    return { trips: scoped.length, days, nights, cities: cities.size };
  }, [trips, year]);
  if (!stats.trips) return null;
  return (
    <Card title="Trips">
      <div className={s.quad}>
        <Metric value={formatNumber(stats.trips)} label="Trips" />
        <Metric value={formatNumber(stats.days)} label="Days away" />
        <Metric value={formatNumber(stats.nights)} label="Nights away" />
        <Metric value={formatNumber(stats.cities)} label="Cities" />
      </div>
    </Card>
  );
}

function RoutesCard({ st, prefs }: { st: FlightStats; prefs: Prefs }) {
  return (
    <Card title="Routes" aside={formatNumber(st.routes.length)}>
      <Ranked
        rows={st.routes.map((r) => ({
          key: r.key,
          title: <Route from={r.a} to={r.b} />,
          detail: [
            r.from?.city && r.to?.city ? `${r.from.city} and ${r.to.city}` : null,
            r.miles != null ? formatDistance(r.miles, prefs) : null,
          ]
            .filter(Boolean)
            .join(', '),
          count: r.count,
        }))}
      />
    </Card>
  );
}

const ALLIANCES: Alliance[] = ['SkyTeam', 'oneworld', 'Star Alliance'];

function AirlinesCard({ st }: { st: FlightStats }) {
  const none = st.flights - ALLIANCES.reduce((n, a) => n + st.alliances[a], 0);
  const rows = ALLIANCES.map((a): { key: string; count: number } => ({
    key: a,
    count: st.alliances[a],
  }))
    .sort((a, b) => b.count - a.count)
    .concat(none > 0 ? [{ key: 'Not in an alliance', count: none }] : []);
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <>
      <Card title="Airlines" aside={formatNumber(st.airlines.length)}>
        <Ranked
          rows={st.airlines.map((a) => ({
            key: a.key,
            title: a.name,
            detail: a.name === a.key ? undefined : a.key,
            count: a.count,
          }))}
        />
      </Card>
      <Card title="Alliances">
        <ul className={s.bars}>
          {rows.map((r) => (
            <li key={r.key} className={s.barRow}>
              <span className={s.barLabel}>{r.key}</span>
              <Meter share={r.count / max} />
              <span className={`${s.rankCount} num`}>{formatNumber(r.count)}</span>
            </li>
          ))}
        </ul>
        <p className={s.note}>By each airline’s alliance today.</p>
      </Card>
    </>
  );
}

function AirportsCard({ st }: { st: FlightStats }) {
  return (
    <Card title="Airports" aside={formatNumber(st.airports.length)}>
      <Ranked
        rows={st.airports.map((a) => ({
          key: a.key,
          lead: <span className={`${s.code} wide`}>{a.key}</span>,
          title: a.airport?.city ?? a.key,
          detail: a.airport?.countryCode ? countryName(a.airport.countryCode) : undefined,
          count: a.count,
        }))}
      />
    </Card>
  );
}

function CountriesCard({ st }: { st: FlightStats }) {
  const n = st.countries.length;
  return (
    <Card title="Countries">
      <div className={s.pair}>
        <Metric big value={`${n} of ${COUNTRIES_TOTAL}`} label="Countries" />
        <Metric big value={pct(n / COUNTRIES_TOTAL)} label="Of the world" />
      </div>
      {st.continents.length > 0 && (
        <ul className={s.continents}>
          {st.continents.map((c) => (
            <li key={c.continent} className={s.continent}>
              <span>{c.continent}</span>
              <span className="num muted">
                {c.total ? `${c.visited} of ${c.total}` : c.visited}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Ranked
        rows={st.countries.map((c) => ({
          key: c.key,
          lead: <span className={s.flag}>{flagEmoji(c.key)}</span>,
          title: countryName(c.key),
          count: c.count,
        }))}
      />
    </Card>
  );
}

function AircraftCard({ st }: { st: FlightStats }) {
  const top = st.aircraft[0];
  const known = st.aircraft.reduce((n, a) => n + a.count, 0);
  return (
    <Card
      title="Aircraft"
      aside={st.aircraft.length ? formatNumber(st.aircraft.length) : undefined}
    >
      {top ? (
        <>
          <Metric value={top.key} label="Flown most" note={plural(top.count, 'flight')} />
          <Ranked rows={st.aircraft.map((a) => ({ key: a.key, title: a.key, count: a.count }))} />
          <p className={s.note}>From {plural(known, 'flight')} with an aircraft type.</p>
        </>
      ) : (
        <p className={s.note}>No aircraft types on these flights.</p>
      )}
    </Card>
  );
}

function FlightStats({
  switcher,
  records: given,
  friend,
}: {
  switcher: ReactNode;
  /** A friend's flights instead of mine. */
  records?: FlightRecord[];
  friend?: string;
}) {
  const items = useStore(selectLiveItems);
  const places = useStore(selectPlaceLookup);
  const live = useStore((st) => st.tables.flightLive);
  const prefs = useStore(selectPrefs);
  const [year, setYear] = useState<number | null>(null);

  // Read every flight once, then add up each year the first time it's picked.
  const mine = useMemo(
    () => (given ? [] : flightRecords({ items, places, live, now: now() })),
    [items, places, live, given],
  );
  const records = given ?? mine;
  const years = useMemo(() => flightYears(records), [records]);
  const statsFor = useMemo(() => {
    const cache = new Map<number | null, FlightStats>();
    return (y: number | null) => {
      let hit = cache.get(y);
      if (!hit) {
        hit = flightStats(records, y, now());
        cache.set(y, hit);
      }
      return hit;
    };
  }, [records]);
  const picked = year != null && !years.includes(year) ? null : year;
  const st = statsFor(picked);

  // The map keeps one view across years: framed on every airport ever flown to.
  const frame = useMemo(
    () =>
      statsFor(null).routes.flatMap((r) =>
        r.from && r.to
          ? [
              { lat: r.from.lat, lon: r.from.lon },
              { lat: r.to.lat, lon: r.to.lon },
            ]
          : [],
      ),
    [statsFor],
  );
  const legs = useMemo(
    () =>
      st.routes.flatMap((r): TripLeg[] =>
        r.from && r.to
          ? [
              {
                itemId: r.key,
                kind: 'flight',
                from: { lat: r.from.lat, lon: r.from.lon, code: r.a, name: r.from.city ?? r.a },
                to: { lat: r.to.lat, lon: r.to.lon, code: r.b, name: r.to.city ?? r.b },
              },
            ]
          : [],
      ),
    [st],
  );

  if (!years.length)
    return (
      <Page title="Stats" wide>
        {switcher}
        <EmptyState
          icon="chart-bar"
          title={friend ? `No flights from ${friend} yet` : 'Your flight stats will show here'}
        >
          <p>Once you’ve flown, you’ll see your routes, airlines, airports and countries.</p>
        </EmptyState>
        {!friend && <TripsCard year={null} />}
      </Page>
    );

  const days = st.minutes / 1440;
  return (
    <Page title="Stats" wide>
      {switcher}
      <div className={s.root}>
        <fieldset className={s.chips}>
          <legend className="sr-only">Year</legend>
          {[null, ...years].map((y) => (
            <button
              key={y ?? 'all'}
              type="button"
              className={`${s.chip} num`}
              aria-pressed={y === picked}
              onClick={() => setYear(y)}
            >
              {y ?? 'All time'}
            </button>
          ))}
        </fieldset>

        <section className={s.hero}>
          <button
            type="button"
            className={s.map}
            onClick={() => navigate('/stats/map')}
            aria-label="Open the Flight Map"
          >
            <MiniMap
              key={picked ?? 'all'}
              legs={legs}
              frame={frame}
              width={720}
              height={300}
              variant="cover"
              draw
            />
            <span className={s.mapHint}>
              <Icon name="arrows-out-simple" size={14} />
              Map
            </span>
          </button>
          <div className={s.summary}>
            <Metric big value={formatNumber(st.flights)} label="Flights" />
            <Metric big value={short(days)} label="Days flying" />
            <Metric
              big
              value={compact.format(prefs.units === 'mi' ? st.miles : st.miles * 1.609344)}
              label={prefs.units === 'mi' ? 'Miles' : 'Kilometers'}
            />
            <Metric big value={formatNumber(st.countries.length)} label="Countries" />
          </div>
          {st.countries.length > 0 && (
            <p className={s.flags}>
              {st.countries.map((c) => (
                <span
                  key={c.key}
                  title={countryName(c.key)}
                  role="img"
                  aria-label={countryName(c.key)}
                >
                  {flagEmoji(c.key)}
                </span>
              ))}
            </p>
          )}
        </section>

        <div className={s.columns}>
          <div className={s.column}>
            <TimeCard st={st} prefs={prefs} />
            <FlightsCard st={st} prefs={prefs} />
            <FlightTimeCard st={st} prefs={prefs} />
            <DelaysCard st={st} prefs={prefs} />
            <SeatCard st={st} />
            {!friend && <TripsCard year={picked} />}
          </div>
          <div className={s.column}>
            <RoutesCard st={st} prefs={prefs} />
            <AirlinesCard st={st} />
            <AirportsCard st={st} />
            <CountriesCard st={st} />
            <AircraftCard st={st} />
          </div>
        </div>
      </div>
    </Page>
  );
}

type View = 'travel' | 'flights';
const VIEW_KEY = 'trips.statsView';

/** Two sets of stats: travel overall, and flights in depth. The last one picked stays picked. */
export function StatsScreen() {
  const [view, setView] = useState<View>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === 'travel' ? 'travel' : 'flights';
    } catch {
      return 'flights';
    }
  });
  const switcher = (
    <Segmented
      label="Which stats"
      value={view}
      onChange={(v: View) => {
        setView(v);
        try {
          localStorage.setItem(VIEW_KEY, v);
        } catch {
          // Private mode: it just won't be remembered.
        }
      }}
      options={[
        { value: 'travel', label: 'Travel' },
        { value: 'flights', label: 'Flights' },
      ]}
    />
  );
  return view === 'travel' ? (
    <TravelStats switcher={switcher} />
  ) : (
    <PeopleStats segmented={switcher} />
  );
}

/** Flight stats for me or a friend who shares theirs. */
function PeopleStats({ segmented }: { segmented: ReactNode }) {
  const [friends] = useFriends();
  const linked = friends.filter((f) => f.status === 'linked');
  const [who, setWho] = useState<string>('me');
  const picked = linked.find((f) => f.id === who) ?? null;
  const { data, error } = useFriendFlights(picked?.id ?? null);
  const switcher = (
    <>
      {segmented}
      {linked.length > 0 && (
        <fieldset className={s.chips}>
          <legend className="sr-only">Whose stats</legend>
          {[{ id: 'me', name: 'Me' }, ...linked].map((f) => (
            <button
              key={f.id}
              type="button"
              className={s.chip}
              aria-pressed={(picked?.id ?? 'me') === f.id}
              onClick={() => setWho(f.id)}
            >
              {f.name ?? 'Friend'}
            </button>
          ))}
        </fieldset>
      )}
    </>
  );
  if (!picked) return <FlightStats switcher={switcher} />;
  if (data?.stats)
    return <FlightStats switcher={switcher} records={data.records} friend={data.name} />;
  return (
    <Page title="Stats" wide>
      {switcher}
      <EmptyState icon="users" title={picked.name ?? 'Your friend'}>
        <p>{data ? `${data.name} hasn't shared flight stats with you.` : (error ?? 'Loading…')}</p>
      </EmptyState>
    </Page>
  );
}
